import { api } from 'api';
import {
  BSB_NUMBER_LENGTH,
  FORM_ADD_PAYTO_ACCOUNT_NUMBER_NAME,
  FORM_ADD_PAYTO_BANK_NAME_NAME,
  FORM_ADD_PAYTO_BSB_FIELD,
  FORM_ADD_PAYTO_BSB_NAME,
} from 'features/to/const';
import usePaytoAPI from 'features/to/hooks/payment/usePaytoAPI';
import usePaytoStore from 'features/to/hooks/payment/usePaytoStore';
import {
  checkMaintenance,
  resolveSystemErrorContent,
  resolveSystemErrorTitle,
} from 'helpers/maintenance';
import { isEmpty } from 'lodash';
import React, { useEffect, useRef } from 'react';
import { SET_SHOW_LOADING } from 'store/action';
import { stringUtils } from 'utils';

const withCheckBSBNumber = (Component) => {
  const CheckBSBNumberComponent = (props) => {
    const {
      information,
      setInformation,
      setValidation,
      setShowFormInputDetails,
      isReadOnly,
      onPaytoSystemError,
    } = props || {};

    const {
      paytoAccountInformation,
      dispatch,
      setPaytoCreateLimit,
      setPaytoAmendLimit,
      setPaytoBsbNumberCheckPending,
    } = usePaytoStore();
    const { payloadTransformFromPaytoAccountInformation } = usePaytoAPI();

    // Generation guard: bumped whenever a new BSB check is armed (or the
    // in-flight one is superseded by an empty/partial BSB edit) so a stale
    // response arriving after a newer edit cannot apply its result.
    const requestGenerationRef = useRef(0);

    // Ref-counted guard (DEV-1401) so the per-call
    // paytoBsbNumberCheckPending signal only clears once none of THIS
    // HOC's own in-flight checks remain -- mirrors the pattern already
    // used in withCheckBsbAccount.jsx / withCheckMobileNumber.jsx.
    // Deliberately separate from the SET_SHOW_LOADING dispatch below,
    // which is the shared global flag row #5 says must not be reused for
    // this purpose.
    const pendingRequestsRef = useRef(0);

    const onCheckBSBNumberValidation = async (
      value,
      name,
      information,
      generation
    ) => {
      pendingRequestsRef.current += 1;
      setPaytoBsbNumberCheckPending(true);

      try {
        dispatch({ type: SET_SHOW_LOADING, payload: true });

        let { data } = isReadOnly
          ? {
              data: payloadTransformFromPaytoAccountInformation(information),
            }
          : await api.validateBSBVer2(value);

        if (requestGenerationRef.current !== generation) {
          return;
        }

        if (data) {
          const {
            bsb,
            bankName,
            bankCode,
            supportsPayTo,
            createPayToLimit,
            amendPayToLimit,
          } = data || {};
          const message = bsb
            ? supportsPayTo
              ? ''
              : FORM_ADD_PAYTO_BSB_FIELD?.msgNumberInvalid2
            : FORM_ADD_PAYTO_BSB_FIELD?.msgNumberInvalid;

          information = {
            ...information,
            [FORM_ADD_PAYTO_BSB_NAME]: stringUtils.replaceAllCharacter(bsb),
            [FORM_ADD_PAYTO_BANK_NAME_NAME]: bankName || '',
            bankCode,
            // DEV-1401: authoritative "confirmed valid" gate for the
            // maxAmount dropdown, mirroring confirmedPayId/confirmedPayIdType
            // in withCheckMobileNumber.jsx. Unlike bankCode (read by other,
            // PayTo-support-agnostic consumers -- usePaytoAPI.jsx,
            // GuidelineIframeModal, withAddPaytoBankVerified.jsx -- so left
            // untouched), this is set only when the resolved BSB actually
            // supports PayTo, and is nulled on every path below that doesn't
            // reconfirm it, so an edit away from a confirmed BSB can't leave
            // the dropdown gated on stale data.
            confirmedBsb:
              bsb && supportsPayTo
                ? stringUtils.replaceAllCharacter(bsb)
                : null,
          };

          setInformation(information);

          setValidation((prev) => ({
            ...prev,
            [name]: message,
          }));

          // DEV-1401: only adopt the limit when the BSB actually resolved
          // and supports PayTo -- an unsupported or unresolved BSB should
          // not overwrite a previously-established authoritative limit with
          // a value from a response that isn't actually usable.
          if (bsb && supportsPayTo) {
            setPaytoCreateLimit(createPayToLimit);
            setPaytoAmendLimit(amendPayToLimit);
          }

          setShowFormInputDetails(true);
        }
      } catch (error) {
        checkMaintenance(error);
        console.error(error?.message);

        if (requestGenerationRef.current !== generation) {
          return;
        }

        // DEV-1401: additive -- a genuine transport/system failure also
        // surfaces the local system-error overlay (see
        // PaytoSystemErrorOverlay.jsx), layered on top of the existing
        // inline "invalid" handling below rather than replacing it, so the
        // field stays poisoned (isAllowSubmit-blocking) regardless of the
        // overlay's own dismissal. A successful (2xx) business result never
        // reaches this catch block, so this cannot affect a valid BSB; a 4xx
        // business result does reach it, and is excluded by the gate below.
        //
        // httpErrors is gated on status >= 500 only (mirrors the existing
        // DEV-1320 precedent in Discount/func/index.js) -- a 4xx, including
        // a 404 for an unknown/invalid BSB, is a business-validation
        // response, not a system failure, and must fall through to the
        // existing inline "invalid" message below untouched. 503 stays
        // excluded regardless via resolveSystemErrorContent's own
        // MAINTENANCE_HTML_CODE branch (checkMaintenance above already owns
        // that case).
        const failureStatus = error?.response?.status;
        const isServerSystemFailure =
          typeof failureStatus === 'number' && failureStatus >= 500;
        const systemErrorContent = resolveSystemErrorContent(error, {
          httpErrors: isServerSystemFailure,
        });
        if (systemErrorContent != null) {
          onPaytoSystemError?.(
            resolveSystemErrorTitle(error, {
              httpErrors: isServerSystemFailure,
            }),
            systemErrorContent,
          );
        }

        setInformation({
          ...information,
          confirmedBsb: null,
        });

        const message = FORM_ADD_PAYTO_BSB_FIELD?.msgNumberInvalid;
        setValidation((prev) => ({
          ...prev,
          [name]: message,
        }));

        setShowFormInputDetails(false);
      } finally {
        // DEV-1401: unconditional -- must clear even when the generation
        // guard above caused an early return, otherwise a superseded
        // request would leave the pending signal stuck true forever.
        pendingRequestsRef.current -= 1;
        if (pendingRequestsRef.current <= 0) {
          setPaytoBsbNumberCheckPending(false);
          // DEV-1401: moved here (was previously duplicated inline in both
          // the success and catch branches, never in this shared finally)
          // -- that duplication left three paths (this generation-guard
          // early return, the success generation-guard early return above,
          // and a falsy-`data` success response) permanently leaking
          // isLoading=true. Gated on the same ref-count guard as the
          // pending flag it sits beside, so a newer in-flight request's own
          // loading state cannot be prematurely cleared by an older one.
          dispatch({ type: SET_SHOW_LOADING, payload: false });
        }
      }
    };

    const handleCheckBSBNumber = () => {
      requestGenerationRef.current += 1;
      const generation = requestGenerationRef.current;
      onCheckBSBNumberValidation(
        information?.[FORM_ADD_PAYTO_BSB_NAME],
        FORM_ADD_PAYTO_BSB_NAME,
        information,
        generation
      );
    };

    useEffect(() => {
      const bsbNumber = stringUtils.replaceAllCharacter(
        information?.[FORM_ADD_PAYTO_BSB_NAME]
      );

      if (isEmpty(bsbNumber)) {
        requestGenerationRef.current += 1;
        setShowFormInputDetails(false);
        setInformation({
          ...information,
          // DEV-1401: this branch previously left FORM_ADD_PAYTO_BANK_NAME_NAME
          // data (only its validation message) and confirmedBsb stale when the
          // BSB field was emptied -- clearing both here closes that gap.
          [FORM_ADD_PAYTO_BANK_NAME_NAME]: '',
          [FORM_ADD_PAYTO_ACCOUNT_NUMBER_NAME]: '',
          confirmedBsb: null,
        });
        setValidation((prev) => ({
          ...prev,
          [FORM_ADD_PAYTO_BANK_NAME_NAME]: '',
          [FORM_ADD_PAYTO_ACCOUNT_NUMBER_NAME]: '',
        }));
        return;
      }

      if (!isEmpty(bsbNumber) && bsbNumber?.length === BSB_NUMBER_LENGTH) {
        handleCheckBSBNumber();
        return;
      }

      // A partial (non-empty, non-full-length) BSB in progress: clear the
      // now-stale dependent fields (bank name, account number) resolved
      // from whatever BSB was previously complete. Do NOT also clear
      // FORM_ADD_PAYTO_BSB_NAME itself here -- that's the field the user
      // is actively typing into; wiping it on every partial keystroke
      // made typing a BSB character-by-character impossible (it reset,
      // then immediately showed the base input's own "too short" message
      // for whatever length was last committed before the wipe).
      requestGenerationRef.current += 1;
      const newInformation = {
        ...information,
        [FORM_ADD_PAYTO_BANK_NAME_NAME]: '',
        [FORM_ADD_PAYTO_ACCOUNT_NUMBER_NAME]: '',
        // DEV-1401: invalidate a prior confirmation the instant the user
        // edits away from it, closing the stale-maxAmount-dropdown gap.
        confirmedBsb: null,
      };
      setInformation(newInformation);
      setValidation((prev) => ({
        ...prev,
        [FORM_ADD_PAYTO_BANK_NAME_NAME]: '',
        [FORM_ADD_PAYTO_ACCOUNT_NUMBER_NAME]: '',
      }));
    }, [information?.[FORM_ADD_PAYTO_BSB_NAME]]);

    const newProps = {
      ...props,
    };
    return <Component {...newProps} />;
  };
  return CheckBSBNumberComponent;
};

export default withCheckBSBNumber;
